import {
  Button, DataProvider, useGetManyAggregate, useRecordContext,
} from 'react-admin';
import {
  fetchRelatedRecords, useRedirect, useTranslate,
} from 'ra-core';
import { Dialog, DialogActions, DialogTitle } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import { useMemo, useState } from 'react';
import { get } from 'lodash';
import { Asset } from '@x-guard/xgac-types/xgac';
import { AssetTableInDialog } from '../AssetTableInDialog';
import { assetExporter } from '../../utils/customExporter';
import { xgacDataProvider } from '../../dataProviders/xgacDataProvider';
import { RaXgacRecord } from '../../lib/RaXgacRecord';

type AssetsInDialogButtonProps = ({
  resource: 'asset-groups';
  hasExporter?: boolean;
  nameColumnLabel?: never;
} | {
  resource: 'certificates';
  nameColumnLabel: string;
  hasExporter?: never;
} | {
  resource: 'security-inventory-categories';
  nameColumnLabel: string;
  hasExporter?: never;
}) & {
  variant?: 'bhvk' | 'xgac';
  source: string;
  label: string;
  buttonLabel?: string;
  className?: string;
  closeClassName?: string;
};

export const AssetsInDialogButton = (props: AssetsInDialogButtonProps) => {

  const translationResource = props.resource === 'certificates' ? 'certification-categories' : props.resource;
  const translate = useTranslate();
  const redirect = useRedirect();
  const [dialogOpen, setDialogOpen] = useState(false);
  const record = useRecordContext();

  const assetIds = useMemo(() => {

    const rawItems = get(record, props.source) ?? [];
    return rawItems.map((item: { _id: string } | string) => {

      if (typeof item === 'string') {

        return item;

      }
      return item._id;

    });

  }, [record, props.source]);

  const showResponsibles = props.resource === 'certificates' || props.resource === 'security-inventory-categories';
  const hasOneResponsible = showResponsibles && assetIds.length === 1;
  const hasNoResponsibles = showResponsibles && assetIds.length === 0;

  // load when opening a dialog or when there is only one responsible, whose name needs to be previewed in the button
  const { data: assets = [], isPending: loading } = useGetManyAggregate<RaXgacRecord<Asset>>('all-assets', {
    ids: assetIds,
    meta: { sort: 'name' },
  }, {
    enabled: assetIds.length > 0 && (dialogOpen || hasOneResponsible),
  });

  let buttonLabel = translate(props.buttonLabel || 'general.text.show_persons', { length: assetIds.length });
  if (hasNoResponsibles) buttonLabel = translate('general.text.nobody_click_to_fix');
  else if (hasOneResponsible && assets[0]) buttonLabel = assets[0].name;

  if (!record) return null;

  const openDialog = () => {

    setDialogOpen(true);

  };

  const closeDialog = () => {

    setDialogOpen(false);

  };

  const exportAssets = async () => {

    await assetExporter(assets, fetchRelatedRecords(xgacDataProvider as any as DataProvider));

  };

  return (<>
    <Button
      variant={'contained'}
      disabled={hasOneResponsible && loading}
      color={hasNoResponsibles ? 'error' : 'primary'}
      onClick={assetIds.length ? openDialog : () => redirect('edit', props.resource, record.id)}
      label={buttonLabel}
      className={props.className}
      sx={{ whiteSpace: 'nowrap', width: showResponsibles ? '100%' : undefined }}
    />
    <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="lg" fullWidth>
      <DialogTitle className="flex-in-between">
        <>
          {`${translate(`resources.${translationResource}.text.title_single`)} ${record.name}`}
        </>
        <div>
          {props.hasExporter && (
            <Button
              label="ra.action.export"
              startIcon={<FileDownloadIcon/>}
              onClick={exportAssets}
              variant="contained"
              sx={{ marginRight: '10px' }}
            />
          )}
          <Button
            label="resources.asset-groups.text.add_person"
            startIcon={<AddIcon/>}
            onClick={() => redirect('edit', props.resource, record.id)}
            variant="contained"
            sx={{ backgroundColor: '#4dbd74' }}
          />
        </div>
      </DialogTitle>
      <AssetTableInDialog assets={assets} loading={loading} nameColumnLabel={props.nameColumnLabel} variant={props.variant}/>
      <DialogActions>
        <Button
          onClick={closeDialog}
          label={translate('general.text.close')}
          {...(props.closeClassName
            ? {
              className: props.closeClassName,
              variant: 'contained' as const,
              size: 'medium' as const,
            }
            : {})}
        />
      </DialogActions>
    </Dialog>
  </>);

};
